import React, { useEffect, useState } from 'react';
import { getTrialInfo } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Banner de Trial (Fase 3).
 *
 * Exibe um banner informativo quando o tenant está com `status='trial'`,
 * mostrando quantos dias restam até o fim do período de teste. A cor
 * escala com a urgência:
 *
 *   14-7 dias  → info azul     (dismissível por sessão)
 *   6-3 dias   → warning laranja (dismissível por sessão)
 *   2-0 dias   → critical vermelho (NÃO dismissível)
 *   expirado   → blocked vermelho (NÃO dismissível)
 *
 * Quando expirado, as operações de escrita já são bloqueadas pelo
 * middleware `trialGuard` no backend (HTTP 402). Este banner apenas
 * comunica visualmente + oferece CTA pra contatar vendas.
 *
 * Uso: montado em TenantLayout, logo acima do QuotaBanner.
 */

// Email de vendas — email institucional do AtendoApp. Pode virar
// configurável via master_settings.support_email no futuro.
const SALES_EMAIL = 'comercial@atendoapp.com';
const DISMISS_KEY = 'atendoapp:trial_banner_dismissed';

function buildMailto(tenantName) {
  const subject = encodeURIComponent(`Upgrade de plano — ${tenantName || 'cliente'}`);
  const body = encodeURIComponent(
    `Olá,\n\nMeu nome é ________ e gostaria de conversar sobre a contratação de um plano ` +
    `para ${tenantName || 'minha conta'}.\n\nObrigado!`
  );
  return `mailto:${SALES_EMAIL}?subject=${subject}&body=${body}`;
}

export default function TrialBanner() {
  const { user } = useAuth();
  const tenantName = user?.tenant?.name;

  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );

  useEffect(() => {
    // A rota retorna { ok: true, trial_info: {...} }.
    getTrialInfo()
      .then(r => setInfo(r.data?.trial_info || null))
      .catch(() => {});
  }, []);

  if (!info) return null;

  // Prioridade 1: cancelamento pendente (sobrepoe qualquer trial info).
  // O usuario clicou "cancelar" e precisa ver um lembrete constante ate
  // a data efetiva — sem opcao de dispensar. Ao clicar em "Ver opcoes",
  // vai pra /app/settings onde tem o botao de reverter.
  if (info.cancel_pending) {
    const days = info.cancel_days_remaining;
    const title = '⏳ Cancelamento agendado';
    const message = days === 0
      ? 'Sua assinatura será desativada hoje. Acesse Configurações para reverter se mudou de ideia.'
      : days === 1
        ? 'Sua assinatura será desativada amanhã. Acesse Configurações para reverter se mudou de ideia.'
        : `Sua assinatura será desativada em ${days} dias. Você mantém acesso completo até lá — e pode reverter o cancelamento em Configurações a qualquer momento.`;
    return (
      <div
        role="alert"
        className="trial-banner trial-banner--cancel"
        style={{
          background: '#fff7ed',
          border: '1px solid #fdba74',
          borderLeft: '4px solid #fdba74',
          borderRadius: 8,
          padding: '12px 16px',
          margin: '12px 16px 0',
          color: '#9a3412',
          fontSize: 13,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div className="trial-banner__icon" style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>⏳</div>
        <div className="trial-banner__body" style={{ flex: 1, minWidth: 200 }}>
          <div className="trial-banner__title" style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
          <div className="trial-banner__message" style={{ lineHeight: 1.5 }}>{message}</div>
          <div className="trial-banner__actions" style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <a
              href="/app/settings"
              className="trial-banner__cta"
              style={{
                display: 'inline-block',
                background: '#9a3412',
                color: '#fff7ed',
                padding: '6px 14px',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              ↩️ Ver opções em Configurações
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Prioridade 2: banner de trial normal.
  if (info.status !== 'trial') return null;

  const days = info.trial_days_remaining;
  const expired = info.trial_expired;

  // Escala de severidade pelo n. de dias restantes
  let severity;
  if (expired) severity = 'blocked';
  else if (days <= 2) severity = 'critical';
  else if (days <= 6) severity = 'warning';
  else severity = 'info';

  // Críticos não podem ser dispensados — usuário precisa ver.
  const canDismiss = severity === 'info' || severity === 'warning';
  if (canDismiss && dismissed) return null;

  const palette = {
    info:     { bg: '#eff6ff', border: '#93c5fd', text: '#1e3a8a', icon: '🎁', title: 'Seu trial está ativo' },
    warning:  { bg: '#fff7ed', border: '#fdba74', text: '#9a3412', icon: '⏳', title: 'Trial quase no fim' },
    critical: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: '⚠️', title: 'Trial expirando em breve' },
    blocked:  { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: '🚫', title: 'Trial expirado' },
  }[severity];

  let message;
  if (expired) {
    message =
      'Seu período de teste acabou. Algumas operações podem estar bloqueadas. ' +
      'Contrate um plano agora para liberar todas as funcionalidades e manter seus dados ativos.';
  } else if (days === 0) {
    message = 'Seu trial termina HOJE. Contrate um plano para continuar usando sem interrupção.';
  } else if (days === 1) {
    message = 'Falta 1 dia para o fim do trial. Contrate um plano para não perder o acesso.';
  } else {
    message = `Você tem ${days} dias restantes do trial gratuito. Está aproveitando? Converse com nosso time para escolher o plano ideal.`;
  }

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className={`trial-banner trial-banner--${severity}`}
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderLeft: `4px solid ${palette.border}`,
        borderRadius: 8,
        padding: '12px 16px',
        margin: '12px 16px 0',
        color: palette.text,
        fontSize: 13,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        flexWrap: 'wrap',
      }}
      role={severity === 'info' ? 'status' : 'alert'}
    >
      <div className="trial-banner__icon" style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{palette.icon}</div>
      <div className="trial-banner__body" style={{ flex: 1, minWidth: 200 }}>
        <div className="trial-banner__title" style={{ fontWeight: 700, marginBottom: 4 }}>{palette.title}</div>
        <div className="trial-banner__message" style={{ lineHeight: 1.5 }}>{message}</div>
        <div className="trial-banner__actions" style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a
            href="/app/choose-plan"
            className="trial-banner__cta"
            style={{
              display: 'inline-block',
              background: palette.text,
              color: palette.bg,
              padding: '6px 14px',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            📋 Escolher plano agora
          </a>
        </div>
      </div>
      {canDismiss && (
        <button
          onClick={handleDismiss}
          className="trial-banner__dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: palette.text,
            cursor: 'pointer',
            fontSize: 18,
            opacity: 0.7,
            padding: 4,
            lineHeight: 1,
            flexShrink: 0,
          }}
          aria-label="Dispensar alerta"
          title="Dispensar alerta"
        >
          ✕
        </button>
      )}
    </div>
  );
}
